<template>
  <b-modal
    id="modalTaskOpenSubtasks"
    ref="modalTaskOpenSubtasks"
    :no-enforce-focus="true"
    size="lg"
    @hidden="onHidden"
  >
    <div slot="modal-title">
      <i class="fas fa-exclamation-triangle text-warning mr-2" />
      {{ 'Open sub-activities' | translate }}
    </div>

    <div v-if="loading" class="text-center my-3">
      <i class="fas fa-spinner fa-spin" />
    </div>

    <template v-else-if="openSubtasks.length">
      <p class="mb-3">
        {{ 'Close the open first-level sub-activities before closing this activity' | translate }}
      </p>
      <table class="table table-sm mb-0">
        <thead>
          <tr>
            <th>{{ 'Activity' | translate }}</th>
            <th>{{ 'Customer' | translate }}</th>
            <th>{{ 'Assignees' | translate }}</th>
            <th>{{ 'State' | translate }}</th>
            <th class="text-right">{{ 'Actions' | translate }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="subtask in openSubtasks" :key="subtask.id">
            <td class="text-wrap">{{ subtask.name }}</td>
            <td class="text-wrap">{{ subtask.customer_name }}</td>
            <td class="text-wrap text-uppercase">{{ subtask.assignee_names }}</td>
            <td>
              <span
                v-if="subtask.state_name"
                class="badge"
                :style="{ backgroundColor: subtask.state_color || '#6c757d', color: '#fff' }"
              >
                {{ subtask.state_name }}
              </span>
            </td>
            <td class="text-right">
              <b-btn
                variant="outline-success"
                size="sm"
                :disabled="closingId === subtask.id"
                :title="$t('Close')"
                @click="onCloseChildClick(subtask)"
              >
                <i
                  :class="closingId === subtask.id ? 'fas fa-spinner fa-spin' : 'fas fa-flag-checkered'"
                  class="mr-1"
                />
                {{ 'Close' | translate }}
              </b-btn>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <p v-else class="mb-0 text-success">
      <i class="fas fa-check-circle mr-2" />
      {{ 'All sub-activities are closed: you can now close this activity' | translate }}
    </p>

    <div slot="modal-footer">
      <b-btn class="mr-2" variant="default" @click="close">
        {{ 'Cancel' | translate }}
      </b-btn>
      <b-btn
        v-if="!loading && !openSubtasks.length"
        variant="success"
        @click="onCloseActivity"
      >
        <i class="fas fa-flag-checkered mr-2" />
        {{ 'Close activity' | translate }}
      </b-btn>
    </div>

    <task-terminate-modal
      v-if="childToTerminate"
      :task="childToTerminate"
      @terminated="onChildTerminated"
      @close="childToTerminate = null"
    />
    <task-open-subtasks-modal
      v-if="childWithOpenSubtasks"
      :task-id="childWithOpenSubtasks.id"
      @subtask-closed="onSubtaskClosed"
      @all-subtasks-closed="onNestedAllClosed"
      @close="childWithOpenSubtasks = null"
    />
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex'
import { TYPES as TASK_TYPES } from '@/pages/task/task/store'
import TaskTerminateModal from './TaskTerminateModal.vue'

export default {
  name: 'TaskOpenSubtasksModal',
  components: {
    'task-terminate-modal': TaskTerminateModal
  },
  props: {
    taskId: { required: true }
  },
  data () {
    return {
      loading: false,
      openSubtasks: [],
      closingId: null,
      childToTerminate: null,
      childWithOpenSubtasks: null
    }
  },
  mounted () {
    this.$refs.modalTaskOpenSubtasks.show()
    this.fetchOpenSubtasks()
  },
  methods: {
    ...mapActions({
      getOpenSubtaskList: TASK_TYPES.GENERIC.task.task.DETAIL.ACTIONS.getOpenSubtaskList,
      markDone: TASK_TYPES.GENERIC.task.task.DETAIL.ACTIONS.markDone
    }),
    fetchOpenSubtasks () {
      this.loading = true
      return this.getOpenSubtaskList({ parent: this.taskId })
        .then((results) => {
          this.openSubtasks = results || []
        })
        .catch(() => {
          // errore già notificato dallo store (handleError)
        })
        .finally(() => {
          this.loading = false
        })
    },
    onCloseChildClick (subtask) {
      // Stessa catena a 3 vie di onMarkDone: se il figlio ha a sua volta dei
      // figli aperti si propaga ricorsivamente lo stesso meccanismo; se è
      // collegato a un preventivo si apre la modale rata; altrimenti chiusura
      // diretta.
      if (subtask.has_open_subtasks) {
        this.childWithOpenSubtasks = subtask
        return
      }
      this.proceedCloseChild(subtask)
    },
    proceedCloseChild (subtask) {
      const quotationId = subtask.quotation || (subtask.quotation_detail && subtask.quotation_detail.id)
      if (quotationId) {
        this.childToTerminate = Object.assign({}, subtask, { quotation: quotationId })
        return
      }
      this.closingId = subtask.id
      this.markDone({ id: subtask.id })
        .then(() => {
          this.afterChildClosed(subtask)
        })
        .catch(() => {
          // errore già notificato dallo store (handleError)
        })
        .finally(() => {
          this.closingId = null
        })
    },
    onChildTerminated ({ task } = {}) {
      const subtask = this.childToTerminate
      this.childToTerminate = null
      this.afterChildClosed(task || subtask)
    },
    onNestedAllClosed () {
      // I nipoti sono stati chiusi: ora si può chiudere il figlio stesso
      // (salta il controllo has_open_subtasks ormai obsoleto).
      const subtask = this.childWithOpenSubtasks
      this.childWithOpenSubtasks = null
      if (subtask) this.proceedCloseChild(subtask)
    },
    afterChildClosed (subtask) {
      this.$emit('subtask-closed', subtask)
      this.fetchOpenSubtasks()
    },
    onSubtaskClosed (subtask) {
      // Propaga verso l'alto la chiusura avvenuta in un livello annidato.
      this.$emit('subtask-closed', subtask)
    },
    onCloseActivity () {
      this.$emit('all-subtasks-closed', this.taskId)
      this.close()
    },
    close () {
      this.$refs.modalTaskOpenSubtasks.hide()
    },
    onHidden () {
      this.$emit('close')
    }
  }
}
</script>
